/**
 * 时间工具函数
 *
 * @serverOnly false - 可在客户端和服务端使用
 */

import dayjs from "dayjs"

/**
 * 格式化当前日期时间
 * @returns 格式化后的日期时间字符串，格式：YYYY-MM-DD HH:mm:ss:毫秒
 */
export const formatDateTime = (): string => {
  const date = new Date()
  const Y = date.getFullYear()
  let m: string | number = date.getMonth() + 1
  let d: string | number = date.getDate()
  let H: string | number = date.getHours()
  let i: string | number = date.getMinutes()
  let s: string | number = date.getSeconds()
  const ns: string | number = date.getMilliseconds()

  if (m < 10) {
    m = `0${m}`
  }
  if (d < 10) {
    d = `0${d}`
  }
  if (H < 10) {
    H = `0${H}`
  }
  if (i < 10) {
    i = `0${i}`
  }
  if (s < 10) {
    s = `0${s}`
  }

  return `${Y}-${m}-${d} ${H}:${i}:${s}:${ns}`
}

/**
 * 获取日期时间戳
 * @param date - 日期字符串
 * @returns 时间戳（毫秒）
 */
export const getDateTimestamp = (date: string): number => {
  return dayjs(date).valueOf()
}

/**
 * 格式化日期
 * @param date - 日期字符串或时间戳
 * @param fmt - 格式化模板，默认为 "YYYY-MM"
 * @returns 格式化后的日期字符串
 */
export const formatDate = (date: string | number, fmt: string = "YYYY-MM"): string => {
  return dayjs(date).format(fmt)
}

/**
 * 格式化时间戳为英文格式
 * @param timestamp - 时间戳（毫秒）
 * @returns 格式化后的时间字符串，如 "Apr.15, 2025"
 */
export const formatDateEn = (timestamp: number): string => {
  if (!timestamp || isNaN(timestamp)) return ""
  return dayjs(timestamp).format("MMM D, YYYY")
}

/**
 * 格式化时间戳为英文完整格式（包含时间）
 * @param timestamp - 时间戳（毫秒）或时间戳字符串
 * @returns 格式化后的时间字符串，如 "Jul 3, 2025 14:31"
 */
export const formatDateEnWithTime = (timestamp: number | string): string => {
  if (!timestamp || (typeof timestamp === "number" && isNaN(timestamp))) return ""
  // 如果是字符串，尝试转换为数字；如果是秒级时间戳，转换为毫秒
  const ts = typeof timestamp === "string" ? Number(timestamp) : timestamp
  const ms = ts < 10000000000 ? ts * 1000 : ts // 判断是秒还是毫秒
  return dayjs(ms).format("MMM D, YYYY HH:mm")
}

/**
 * 获取日期分组名称
 * @param dateParameter - 时间字符串
 * @returns 日期分组名称，如 "今天"、"昨天"、"七天内"、"30天内" 或 "YYYY年MM月"
 */
export const getDateGroupName = (dateParameter: string): string => {
  const now = dayjs()
  const date = dayjs(dateParameter)

  // 今天
  if (date.isSame(now, "day")) {
    return "今天"
  }

  // 昨天
  if (date.isSame(now.subtract(1, "day"), "day")) {
    return "昨天"
  }

  // 七天内
  if (date.isAfter(now.subtract(7, "day"))) {
    return "七天内"
  }

  // 30天内
  if (date.isAfter(now.subtract(30, "day"))) {
    return "30天内"
  }

  // 超过30天，按年月分组
  return date.format("YYYY年MM月")
}

// 可以用作判断时间是否有效
export const toDate = (input: string | number | undefined): Date | null => {
  if (input == null) return null
  try {
    if (typeof input === "number") {
      // 统一：Unix seconds -> ms
      const d = new Date(input * 1000)
      return isNaN(d.getTime()) ? null : d
    }
    const val = String(input).trim()
    if (/^\d+$/.test(val)) {
      // 统一：纯数字字符串视为 Unix seconds
      const d = new Date(Number(val) * 1000)
      return isNaN(d.getTime()) ? null : d
    }
    // 容错：非纯数字的字符串按可解析日期处理（例如 ISO 字符串）
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}
